import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, Typography, Container, Button, Card, CardContent, Divider, List, ListItem, ListItemIcon, ListItemText, CircularProgress, Chip, useTheme } from '@mui/material';
import { CheckCircleOutline as CheckCircleIcon, ArrowBack as ArrowBackIcon, Payment as PaymentIcon } from '@mui/icons-material';
import { API_BASE_URL } from '../config';

const ToolDetail = () => {
  const { slug } = useParams();
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const theme = useTheme();
  const mode = theme.palette.mode;

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/tools/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error("Tool not found");
        return res.json();
      })
      .then(data => {
        setTool(data);
        setLoading(false);
      })
      .catch(err => {
        console.error("Error fetching tool:", err);
        setLoading(false);
      });
  }, [slug]);

  useEffect(() => {
    if (tool) {
      document.title = `${tool.name} | CoreTechTalks`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", `Purchase and download ${tool.name} on CoreTechTalks. ${tool.description}`);
      }
    }
  }, [tool]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool_slug: slug })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Error connecting to Stripe");
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error("Checkout failed:", e);
      alert(`Checkout Error: ${e.message}\n(Make sure your Stripe Secret Keys are properly set in the backend .env file!)`);
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12} minHeight="calc(100vh - 200px)">
        <CircularProgress color="primary" sx={{ mb: 2 }} />
        <Typography variant="body1" color="text.secondary">Fetching utility details...</Typography>
      </Box>
    );
  }

  if (!tool) {
    return (
      <Box py={12} textAlign="center" minHeight="calc(100vh - 200px)">
        <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>Tool not found.</Typography>
        <Button id="btn-notfound-back-to-tools" component={Link} to="/tools" variant="outlined" startIcon={<ArrowBackIcon />}>
          Back to Tools
        </Button>
      </Box>
    );
  }

  const features = tool.features ? tool.features.split(',') : [];

  return (
    <Box sx={{ flexGrow: 1, py: { xs: 8, md: 12 }, minHeight: 'calc(100vh - 200px)', position: 'relative' }}>
      {/* Background decorations */}
      <Box className="glow-orb" sx={{ width: '400px', height: '400px', top: '-100px', right: '-100px', background: 'rgba(99, 102, 241, 0.08)' }} />
      <Box className="glow-orb" sx={{ width: '300px', height: '300px', bottom: '10%', left: '-150px', background: 'rgba(6, 182, 212, 0.08)' }} />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ mb: 4 }}>
          <Button 
            id="btn-back-to-tools"
            component={Link} 
            to="/tools" 
            variant="text" 
            startIcon={<ArrowBackIcon />} 
            sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
          >
            Back to Tools
          </Button>
        </Box>

        <Card sx={{ p: { xs: 2, md: 4 } }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 4 }}>
              <Box>
                <Typography variant="h1" component="h1" sx={{ mb: 1.5, fontSize: { xs: '32px', md: '44px' } }}>
                  {tool.name}
                </Typography>
                <Chip label="Developer Utility" color="primary" size="small" sx={{ fontWeight: 700, fontSize: '11px' }} />
              </Box>
              <Typography variant="h3" color="primary.main" sx={{ fontWeight: 800, fontFamily: '"Outfit", sans-serif' }}>
                ${tool.price.toFixed(2)}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" sx={{ fontSize: '17px', lineHeight: 1.7, mb: 4 }}>
              {tool.description}
            </Typography>

            <Divider sx={{ my: 4, borderColor: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)' }} />

            <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
              What's Included:
            </Typography>
            <List sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {features.map((feature, idx) => (
                <ListItem key={idx} disableGutters sx={{ py: 0, gap: 1.5 }}>
                  <ListItemIcon sx={{ minWidth: 'auto', color: 'success.main' }}>
                    <CheckCircleIcon sx={{ fontSize: '20px' }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={feature.trim()} 
                    primaryTypographyProps={{ 
                      fontSize: '15px', 
                      fontWeight: 600, 
                      color: 'text.primary' 
                    }} 
                  />
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
              <Button 
                id="btn-purchase-stripe"
                variant="contained" 
                color="primary" 
                size="large" 
                onClick={handleCheckout}
                disabled={checkoutLoading}
                startIcon={<PaymentIcon />}
                sx={{ 
                  px: 6, 
                  py: 1.8, 
                  fontSize: '16px',
                  borderRadius: '12px',
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {checkoutLoading ? 'Preparing Checkout...' : 'Purchase with Stripe'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ToolDetail;
